/**
 * SidebarNavRuntime — rendering primitives for the recursive scope sidebar.
 *
 * Exports:
 *   useCachedState          — useState with external cache persistence
 *   PaginatedExplorerContent — paginated tree of ExplorerNode entries
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SurfaceChart } from "./ChartBlocks.jsx";
import { STIPPLE_SIZE_COMPACT, STIPPLE_DOT_RADIUS_COMPACT } from "../lib/stipple.js";
import { useQuery } from "../lib/dataClient.js";
import StickyFolder, {
  DeferredContent,
  FolderFooter,
  VirtualFolderList,
} from "./StickyFolder.jsx";

// Minimal in-view detector. Used by EntrySparkline to gate the lazy
// per-entry sparkline fetch — without this every row in the pane
// would fire on mount and we'd be back to N parallel requests, just
// happening at render time instead of in apiClient.
function useInView(rootMargin = "100px") {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      // Older browsers: just fire immediately. Cheap fallback.
      setInView(true);
      return undefined;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) if (e.isIntersecting) { setInView(true); break; }
      },
      { rootMargin },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [inView, rootMargin]);
  return [ref, inView];
}

// Shared interactive-row chrome. A -z-10 ::before fills the row behind its
// content (label, icon and sparkline bars all sit above it):
//   - idle + interactive → a subtle gray wash on hover, so every cell gives
//     feedback under the pointer — not just the selected one.
//   - active             → the green-soft fill + 2px inset green bar, and the
//     wash enriches toward green on hover.
// Hover and active share the single ::before, so an active row that's also
// hovered swaps its fill (green-soft → enriched green) rather than stacking
// two tints. The host element must be position:relative (all sidebar rows
// are). Non-interactive rows get the active fill only, no hover affordance.
export function rowChromeClass(active = false, interactive = true) {
  const base =
    "isolate before:absolute before:inset-0 before:-z-10";
  if (active) {
    const parts = [
      base,
      "before:bg-[color:var(--green-soft)] before:shadow-[inset_2px_0_0_var(--green)]",
    ];
    if (interactive) {
      parts.push("hover:before:bg-[color:color-mix(in_srgb,var(--green)_18%,transparent)]");
    }
    return parts.join(" ");
  }
  if (interactive) {
    return `${base} hover:before:bg-[color:var(--gray-fill)]`;
  }
  return "";
}

// ── Cached state hook ─────────────────────────────────────────────
//
// Sidebar dim panes (open/closed, search query, sort, per-entry
// expansions) all flow through here. The shared `cacheRef.current` is
// the in-memory store; App.jsx mirrors it into a ?ui= URL param so a
// shared URL restores the entire sidebar tree state. Each setValue
// fires `cacheRef.current[CACHE_NOTIFY]?.()` so the URL-sync owner
// can debounce serialisation. The notify slot uses a Symbol so it
// doesn't appear in JSON.stringify(cacheRef.current).

export const CACHE_NOTIFY = Symbol.for("dashboard.cache.notify");

export function useCachedState(cacheRef, cacheKey, stateKey, initial) {
  const [value, _setValue] = useState(() => {
    const cached = cacheRef?.current?.[cacheKey]?.[stateKey];
    return cached !== undefined ? cached : initial;
  });

  const latestRef = useRef(value);
  latestRef.current = value;

  const setValue = useCallback((v) => {
    const prev = latestRef.current;
    const next = typeof v === "function" ? v(prev) : v;
    latestRef.current = next;
    if (cacheRef?.current) {
      if (!cacheRef.current[cacheKey]) cacheRef.current[cacheKey] = {};
      cacheRef.current[cacheKey][stateKey] = next;
      cacheRef.current[CACHE_NOTIFY]?.();
    }
    _setValue(next);
  }, [cacheRef, cacheKey, stateKey]);

  return [value, setValue];
}

// ── Trailing-expansion recency ────────────────────────────────────
//
// The sidebar tree lets several dimension folders sit open at once. The
// column view (ScopeColumns) can only show ONE trailing entries column,
// so when the user crosses over it needs to know which open folder was
// the *most recent* expansion. We stamp a monotonic sequence onto the
// dim pane's cache entry when it's opened at the trailing scope level;
// ScopeColumns picks the open pane with the highest stamp. The stamp is
// runtime-only (pruneCacheForUrl drops it), so deep links fall back to
// "any open trailing dim".
let _trailSeqCounter = 0;

export function markTrailExpansion(cacheRef, cacheKey) {
  if (!cacheRef?.current) return;
  if (!cacheRef.current[cacheKey]) cacheRef.current[cacheKey] = {};
  cacheRef.current[cacheKey]._trailSeq = ++_trailSeqCounter;
  cacheRef.current[CACHE_NOTIFY]?.();
}

// ── Visual primitives ─────────────────────────────────────────────

function Chevron({ open = false }) {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" className={["h-[12px] w-[12px] transition-transform", open ? "rotate-90" : ""].join(" ")}>
      <path d="m6 3 5 5-5 5" />
    </svg>
  );
}

function LeafMarker() {
  return (
    <span className="mt-[4px] grid h-[14px] w-[14px] place-items-center text-[color:var(--muted-soft)]" aria-hidden="true">
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="h-[10px] w-[10px]">
        <path d="M4 8h8" />
      </svg>
    </span>
  );
}

// Exported: the canonical entry-row sparkline (share-bar tint behind a
// SurfaceChart line). Reused outside the sidebar wherever dimension
// entries render as rows (e.g. the cluster Detail composition panels),
// so every entry row in the app draws the same picture.
export function EntrySparkline({ sparkline, sparklineLoader, share = 0, active = false }) {
  // Lazy path: sparkline.series is the `null` sentinel — fetch this
  // entry's bucket data only once the row scrolls into view. The
  // share bar still renders unconditionally so the layout doesn't
  // jump when the line arrives.
  const isLazy = sparklineLoader && sparkline?.series === null;
  const [rowRef, inView] = useInView();
  const lazyParams = useMemo(() => sparklineLoader || null, [
    sparklineLoader?.dimKey,
    sparklineLoader?.id,
    sparklineLoader?.parentScopeKey,
    sparklineLoader?.range,
  ]);
  const { data: lazyData } = useQuery(
    "getDimEntrySparkline",
    lazyParams,
    { enabled: Boolean(isLazy && inView && lazyParams) },
  );
  const effectiveSeries = isLazy
    ? (Array.isArray(lazyData?.series) ? lazyData.series : null)
    : (sparkline?.series ?? null);

  if (!sparkline && !share) return null;
  const pct = share ? Math.min(share, 100) : 0;

  // Four render states, in priority order:
  //   1. Loaded sparkline → full envelope w/ line + 0.18 bg tint.
  //   2. Lazy + pending   → full envelope w/ 0.18 bg tint, no line
  //                         (reserves the height so layout doesn't
  //                         shift when the line arrives). Pinned to
  //                         the IntersectionObserver ref.
  //   3. Placeholder      → flat baseline at the line's color, always
  //                         visible (e.g. a signal facet with no
  //                         firings in the scan window — keeps the row
  //                         height and "this carries a sparkline" cue
  //                         constant whether or not it has fired).
  //   4. Genuinely empty  → thin 3px bar at 0.45 opacity (the
  //                         "no-sparkline" treatment for dims that
  //                         don't carry one at all).
  const hasLineToDraw = Array.isArray(effectiveSeries) && effectiveSeries.length > 0;
  const isLoading = Boolean(sparkline?.loading) && !hasLineToDraw;
  const isPlaceholder = Boolean(sparkline?.placeholder);
  const reservesSparklineRow = hasLineToDraw || isLazy || isLoading || isPlaceholder;

  if (!reservesSparklineRow) {
    return (
      <div className="mt-[2px] flex h-[3px] w-full items-center">
        {pct > 0 ? (
          <div
            className="h-full rounded-[1px] bg-[color:var(--green)]"
            style={{ width: `${pct}%`, opacity: 0.45 }}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div ref={isLazy ? rowRef : null} className="relative mt-[1px] w-full px-[2px] py-[1px]">
      {pct > 0 ? (
        <div
          className="absolute inset-0 rounded-[1px] bg-[color:var(--green)]"
          style={{ width: `${pct}%`, opacity: 0.18 }}
        />
      ) : null}
      {hasLineToDraw ? (
        <SurfaceChart
          mode="line"
          series={effectiveSeries}
          stroke={sparkline.stroke}
          fill={sparkline.fill}
          className="relative min-h-[14px] text-[color:var(--plot-ink-soft)]"
          padding={2}
          width={240}
          height={14}
          strokeWidth={1.4}
          haloWidth={3.8}
          fillPattern
          fillPatternSize={STIPPLE_SIZE_COMPACT}
          fillDotRadius={STIPPLE_DOT_RADIUS_COMPACT}
          haloStroke="var(--bg)"
        />
      ) : (isLazy || isLoading) ? (
        // Reserve the same vertical space the sparkline would occupy
        // so the row's height stays constant across loading → loaded.
        // Static muted baseline + a thin highlight that drifts L→R
        // slowly — subtle "fetching" cue, not a loud shimmer.
        // Keyframes in styles.css. Used for both lazy-fetch dims and the
        // signals facet while a scan is recomputing this window.
        <div className="relative flex min-h-[14px] items-center" aria-hidden="true">
          <div
            className="absolute inset-x-0 h-[1px] rounded-[1px]"
            style={{ background: "var(--muted-soft)", opacity: 0.25 }}
          />
          <div
            className="absolute h-[1px] rounded-[1px]"
            style={{
              left: 0,
              right: 0,
              background: "linear-gradient(90deg, transparent 0%, var(--muted-soft) 50%, transparent 100%)",
              backgroundSize: "25% 100%",
              backgroundRepeat: "no-repeat",
              animation: "sparkline-shimmer 2.6s ease-in-out infinite",
              opacity: 0.6,
            }}
          />
        </div>
      ) : sparkline?.ran ? (
        // Evaluated over this window and nothing fired → solid flat baseline
        // in the line's own color ("ran, quiet").
        <div className="relative flex min-h-[14px] items-center" aria-hidden="true">
          <div
            className="absolute inset-x-0 bottom-[2px] h-[1px] rounded-[1px]"
            style={{ background: sparkline?.stroke || "var(--muted-soft)", opacity: 0.35 }}
          />
        </div>
      ) : (
        // Hasn't run yet / waiting for action (a cheap signal before the
        // first scan, or a lookup signal not yet run from its page) →
        // dotted muted baseline, visually distinct from a quiet run.
        <div className="relative flex min-h-[14px] items-center" aria-hidden="true">
          <div
            className="absolute inset-x-0 bottom-[2px] border-t border-dotted"
            style={{ borderColor: "var(--muted-soft)", opacity: 0.6 }}
          />
        </div>
      )}
    </div>
  );
}

// ── Row components ────────────────────────────────────────────────

export function FolderListRow({
  as: Component = "div",
  to,
  onClick,
  active = false,
  expanded = false,
  onToggleExpand = null,
  share = 0,
  title,
  note,
  meta = null,
  metaNote = null,
  className = "",
  children = null,
}) {
  const expandable = Boolean(onToggleExpand);
  const rowAction = onClick || onToggleExpand;
  const sharedShell = "relative grid w-full items-start gap-x-2 bg-transparent pl-[6px] pr-[9px] text-left";
  const sharedGrid = "[grid-template-columns:14px_minmax(0,1fr)]";
  const detailsGrid = "[grid-template-columns:14px_minmax(0,1fr)_auto]";
  const leafInteractive = Boolean(onClick) || to !== undefined || Component === "button";

  const showInlineDetails = share || !expandable;
  const hasInlineRow = showInlineDetails || Boolean(children);

  const contentBody = (
    <>
      <div className="grid min-w-0 gap-0">
        <strong className="text-[12px] leading-[1.08] font-medium line-clamp-2 break-words">{title}</strong>
        {showInlineDetails && !share && note ? (
          <div className="mt-1 text-[11px] leading-[1.2] text-[color:var(--muted-soft)]">{note}</div>
        ) : null}
      </div>
      {meta !== null ? (
        <div className="grid min-w-0 justify-items-end gap-0">
          <strong className="self-start justify-self-end whitespace-nowrap text-[12px] leading-[1.08] font-medium text-[color:var(--ink)]">
            {meta}
          </strong>
          {showInlineDetails && !share && metaNote ? (
            <div className="mt-1 text-[11px] leading-[1.2] text-[color:var(--muted-soft)]">{metaNote}</div>
          ) : null}
        </div>
      ) : null}
      {/* Sparkline / share bar lives on its own row spanning both
          the title and meta columns so it fills the full row width
          rather than getting boxed in alongside the title. Always
          rendered when provided — zero-share rows still want the
          sparkline so the row layout stays consistent. */}
      {children ? (
        <div className="col-span-2 min-w-0">{children}</div>
      ) : null}
    </>
  );

  if (expandable) {
    return (
      <div
        className={[
          sharedShell,
          (active && expanded) || hasInlineRow ? "py-2" : "pt-2",
          rowAction ? "cursor-pointer" : "",
          sharedGrid,
          rowChromeClass(active, Boolean(rowAction)),
          className,
        ].filter(Boolean).join(" ")}
        role={rowAction ? "button" : undefined}
        tabIndex={rowAction ? 0 : undefined}
        onClick={rowAction || undefined}
        onKeyDown={rowAction ? ((event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            rowAction();
          }
        }) : undefined}
      >
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onToggleExpand?.();
          }}
          className="mt-[1px] grid h-[14px] w-[14px] place-items-center text-[color:var(--muted)]"
          aria-label={expanded ? "Collapse" : "Expand"}
        >
          <Chevron open={expanded} />
        </button>
        <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-start gap-[2px] bg-transparent p-0 text-left text-[color:var(--ink)]">
          {contentBody}
        </div>
      </div>
    );
  }

  const interactiveProps = Component === "button"
    ? { type: "button", onClick }
    : {
        ...(to !== undefined ? { to } : {}),
        ...(onClick ? { onClick } : {}),
      };

  return (
    <Component
      {...interactiveProps}
      className={[
        sharedShell,
        "appearance-none py-2",
        leafInteractive ? "cursor-pointer" : "",
        detailsGrid,
        rowChromeClass(active, leafInteractive),
        className,
      ].filter(Boolean).join(" ")}
    >
      <LeafMarker />
      <div className="col-span-2 grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-start gap-[2px]">
        {contentBody}
      </div>
    </Component>
  );
}

function FolderListDetails({
  note,
  metaNote = null,
  children = null,
  active = false,
  open = false,
  onClick = null,
}) {
  if (!note && !metaNote && !children) return null;

  const clickable = Boolean(onClick);
  const Component = clickable ? "button" : "div";
  const interactiveProps = onClick ? { type: "button", onClick } : {};

  return (
    <Component
      {...interactiveProps}
      className={[
        "relative grid w-full items-start gap-x-2 border-0 bg-transparent pl-[6px] pr-[9px] text-left appearance-none",
        "[grid-template-columns:14px_minmax(0,1fr)_auto]",
        (active && open) ? "pt-2 pb-1" : "pb-[4px] pt-0",
        clickable ? "cursor-pointer" : "",
        rowChromeClass(active, clickable),
      ].join(" ")}
    >
      <span aria-hidden="true" />
      {note ? (
        <div className="min-w-0 text-[11px] leading-[1.2] text-[color:var(--muted-soft)]">{note}</div>
      ) : (
        <div />
      )}
      {metaNote ? (
        <div className="justify-self-end text-[11px] leading-[1.2] text-[color:var(--muted-soft)]">
          {metaNote}
        </div>
      ) : null}
      <span aria-hidden="true" />
      {children ? (
        <div className="col-span-2 min-w-0 mt-[4px]">{children}</div>
      ) : null}
    </Component>
  );
}

// ── Tree rendering ────────────────────────────────────────────────

function ExplorerNode({ node }) {
  const rowProps = {
    as: node.as,
    to: node.to,
    onClick: node.onClick,
    active: node.active,
    expanded: node.open,
    onToggleExpand: node.onToggleExpand,
    share: node.share,
    title: node.title,
    note: node.note,
    meta: node.meta,
    metaNote: node.metaNote,
    className: node.className,
  };

  if (!node.onToggleExpand) {
    return (
      <FolderListRow {...rowProps}>
        <EntrySparkline sparkline={node.sparkline} sparklineLoader={node.sparklineLoader} share={node.share} active={node.active} />
      </FolderListRow>
    );
  }

  const content = node.content ?? (
    node.children?.length ? (
      <ExplorerTree nodes={node.children} invalidateKey={node.invalidateKey} />
    ) : null
  );

  // Layout is purely driven by what details panel content exists. If
  // there's a note/metaNote to surface, the row expands to a two-row
  // layout with the sparkline below. Otherwise the sparkline stays
  // inline. Share value plays no role — every row has the same shape
  // regardless of 0% / 100% / 23.4123%.
  const inlineInHeader = !node.note && !node.metaNote;

  return (
    <StickyFolder
      open={node.open}
      active={node.active}
      sticky={node.sticky}
      header={(
        <FolderListRow {...rowProps}>
          {inlineInHeader ? <EntrySparkline sparkline={node.sparkline} sparklineLoader={node.sparklineLoader} share={node.share} active={node.active} /> : null}
        </FolderListRow>
      )}
      details={(
        <FolderListDetails
          note={inlineInHeader ? undefined : node.note}
          metaNote={inlineInHeader ? undefined : node.metaNote}
          active={node.active}
          open={node.open}
          onClick={node.onClick || node.onToggleExpand}
        >
          {!inlineInHeader ? <EntrySparkline sparkline={node.sparkline} sparklineLoader={node.sparklineLoader} active={node.active} /> : null}
        </FolderListDetails>
      )}
      toolbar={node.toolbar ? <FolderToolbar {...node.toolbar} /> : null}
    >
      {node.defer ? (
        <DeferredContent trigger={node.defer.trigger} rows={node.defer.rows ?? 3}>
          {content}
        </DeferredContent>
      ) : (
        content
      )}
    </StickyFolder>
  );
}

function ExplorerTree({ nodes, invalidateKey = "" }) {
  if (!nodes.length) return null;
  return (
    <VirtualFolderList
      items={nodes}
      invalidateKey={invalidateKey}
      renderItem={(node) => <ExplorerNode node={node} />}
    />
  );
}

// ── Paginated explorer ────────────────────────────────────────────

export function PaginatedExplorerContent({
  nodes,
  // Optional server-pagination glue: caller provides the total count,
  // a callback to fetch the next page, and whether a fetch is in flight.
  // When omitted, the component just paginates locally over `nodes`.
  grandTotal = null,
  onLoadMore = null,
  loadingMore = false,
  cacheRef = null,
  cacheKey = "",
  stateKey,
  resetKey,
  pageSize = 12,
  // Bound automatic server-page chaining when de-duplication can make loaded
  // node growth smaller than the raw server-page growth. Infinity preserves
  // the existing behavior for panes whose node count matches the server total.
  maxAutoLoads = Infinity,
  invalidateKey = "",
  emptyMessage = "No results match this filter.",
}) {
  const [visible, setVisible] = useCachedState(cacheRef, cacheKey, stateKey, pageSize);
  const autoLoadsRef = useRef(0);
  const minimumVisible = nodes.reduce((count, node, index) => (
    node.active || node.open ? index + 1 : count
  ), 0);

  useEffect(() => {
    setVisible(Math.max(pageSize, minimumVisible));
    autoLoadsRef.current = 0;
  }, [pageSize, resetKey, setVisible]);

  useEffect(() => {
    setVisible((count) => Math.max(count, pageSize, minimumVisible));
  }, [minimumVisible, pageSize, setVisible]);

  // If `visible` outruns the locally-cached `nodes` and there's more on
  // the server, request the next page. Optimistic — `visible` is bumped
  // before the fetch lands; the slice clamps to nodes.length until the
  // new page arrives.
  useEffect(() => {
    if (!onLoadMore || loadingMore) return;
    if (autoLoadsRef.current >= maxAutoLoads) return;
    if (visible > nodes.length && (grandTotal == null || nodes.length < grandTotal)) {
      autoLoadsRef.current += 1;
      onLoadMore();
    }
  }, [visible, nodes.length, grandTotal, onLoadMore, loadingMore, maxAutoLoads]);

  if (!nodes.length && !loadingMore) {
    return (
      <div className="mx-[6px] border border-dashed border-[color:var(--line-soft)] px-[10px] py-[10px] text-[11.5px] leading-[1.3] text-[color:var(--muted-soft)]">
        {emptyMessage}
      </div>
    );
  }

  const total = grandTotal != null ? grandTotal : nodes.length;
  const visibleCount = Math.min(visible, nodes.length);

  return (
    <>
      <ExplorerTree nodes={nodes.slice(0, visibleCount)} invalidateKey={invalidateKey} />
      <FolderFooter
        visible={visibleCount}
        total={total}
        onLoadMore={() => {
          // An explicit request starts a new bounded auto-load allowance. If
          // the cached visible target is already at the raw total, fetch one
          // page directly because setVisible would otherwise be a no-op.
          autoLoadsRef.current = 0;
          const nextVisible = Math.min(visible + pageSize, total);
          if (nextVisible === visible && onLoadMore && !loadingMore && nodes.length < total) {
            autoLoadsRef.current = 1;
            onLoadMore();
          } else {
            setVisible(nextVisible);
          }
        }}
        pageSize={pageSize}
        className="pt-[4px]"
      />
    </>
  );
}
